/**
 * 日付フォーマット用ユーティリティ関数
 */

/**
 * 曜日の日本語配列
 */
export const WEEKDAYS_JP = ['日', '月', '火', '水', '木', '金', '土'] as const;

/**
 * 日付をYYYY-MM-DD形式にフォーマット
 * @param date - フォーマットする日付
 * @returns YYYY-MM-DD形式の文字列
 */
export const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 日付をYYYY年MM月DD日(曜日)形式にフォーマット
 * @param date - フォーマットする日付
 * @param suffix - 末尾に追加する文字列（デフォルト: 空文字）
 * @returns フォーマットされた文字列
 */
export const formatDateToJapanese = (date: Date, suffix = ''): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayOfWeek = WEEKDAYS_JP[date.getDay()];
  return `${year}年${month}月${day}日(${dayOfWeek})${suffix}`;
};

/**
 * YYYY-MM-DD形式の文字列をYYYY年M月D日(曜日)形式にフォーマット
 *
 * new Date('YYYY-MM-DD') はUTC深夜として解釈されタイムゾーン依存で日付がずれるため、
 * 文字列を分解してローカル日付を組み立てる。
 *
 * @param dateStr - YYYY-MM-DD形式の日付文字列
 * @returns フォーマットされた文字列。値なし・不正な日付の場合は空文字
 */
export const formatISODateToJapanese = (dateStr?: string | null): string => {
  if (!dateStr) return '';

  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (!matched) return '';

  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);
  const date = new Date(year, month - 1, day);

  // 2026-02-30 のような繰り上がりを弾く
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return '';
  }

  return formatDateToJapanese(date);
};

/**
 * 日付をYYYY/MM/DD形式にフォーマット（日付の0埋めあり）
 * @param date - フォーマットする日付
 * @returns YYYY/MM/DD形式の文字列
 */
export const formatDateToSlash = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

/**
 * 日付文字列を「日\n曜日」形式にフォーマット（グラフX軸用）
 * @param dateStr - ISO形式の日付文字列
 * @returns 「日\n曜日」形式の文字列
 */
export const formatDateForChartAxis = (dateStr: string): string => {
  const date = new Date(dateStr);
  const day = date.getDate();
  const weekday = WEEKDAYS_JP[date.getDay()];
  return `${day}\n${weekday}`;
};

/**
 * HomePageで使用する日付情報を取得
 * @param date - 対象の日付
 * @returns full（「YYYY年MM月DD日(曜日)のご来所」）とshort（「YYYY/MM/DD」）
 */
export const getHomeDateInfo = (date: Date) => {
  return {
    full: formatDateToJapanese(date, 'のご来所'),
    short: formatDateToSlash(date),
  };
};
